import { apiSlice } from "../store/app.api";
import { supabase } from "../utils/API";

export type ExistingAttachment = {
  id: string;
  // make both optional so the same type works for both tables
  news_id?: string;
  events_id?: string;
  type: "IMAGE" | "FILE";
  file_url: string;
  alias?: string | null;
};

const updatesAPI = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getNews: build.query<any, any>({
      queryFn: async () => {
        let { data } = await supabase
          .from("news")
          .select(
            `
        *,
        attachments:news_attachments (*)
      `
          )
          .order("created_at", { ascending: false });

        return { data: data ?? [] }; // ✅ This guarantees it's not null
      },
      providesTags: ["updates"],
    }),

    getEvents: build.query<any, any>({
      queryFn: async () => {
        let { data } = await supabase
          .from("events")
          .select(
            `
        *,
        attachments:events_attachments (*)
      `
          )
          .order("created_at", { ascending: false });

        return { data: data ?? [] }; // ✅ This guarantees it's not null
      },
      providesTags: ["updates"],
    }),

    //Mutations
    addNews: build.mutation<
      any,
      {
        title: string;
        content: string;
        date: Date;
        images: File[];
        linkAlias: string;
        linkUrl: string;
      }
    >({
      queryFn: async ({ title, content, date, images, linkAlias, linkUrl }) => {
        try {
          // 1. Insert the news entry
          const { data: newsData, error: newsError } = await supabase
            .from("news")
            .insert([{ title, content, date }])
            .select()
            .single();

          if (newsError) throw newsError;

          const newsId = newsData.id;

          // 2. Upload images to Supabase storage
          const imageAttachmentPromises = images.map(async (image) => {
            const filePath = `news/${Date.now()}_${image.name}`;

            const { error: storageError } = await supabase.storage
              .from("images")
              .upload(filePath, image);

            if (storageError) throw storageError;

            const { publicUrl } = supabase.storage
              .from("images")
              .getPublicUrl(filePath).data;

            return {
              news_id: newsId,
              file_url: publicUrl,
              type: "IMAGE",
              alias: null,
            };
          });

          const imageAttachments = await Promise.all(imageAttachmentPromises);

          // 3. Prepare the link attachment
          const linkAttachment = {
            news_id: newsId,
            file_url: linkUrl,
            type: "FILE",
            alias: linkAlias,
          };

          // 4. Insert all attachments (images + link)
          const allAttachments = [...imageAttachments, linkAttachment];

          const { error: attachmentError } = await supabase
            .from("news_attachments")
            .insert(allAttachments);

          if (attachmentError) throw attachmentError;

          return { data: newsData };
        } catch (error: any) {
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: ["updates"],
    }),

    updateNews: build.mutation<
      any,
      {
        id: string;
        title: string;
        content: string;
        date: Date;
        // existing images the user chose to KEEP (by id)
        keepImageIds: string[];
        // new images to UPLOAD
        newImages: File[];
        // link: pass either an existing link attachment id to keep/update, or null to remove
        linkAttachment?: { id?: string; alias: string; url: string } | null;
      }
    >({
      queryFn: async ({
        id,
        title,
        content,
        date,
        keepImageIds,
        newImages,
        linkAttachment,
      }) => {
        try {
          // 1) Read current attachments
          const { data: current } = await supabase
            .from("news_attachments")
            .select("*")
            .eq("news_id", id);

          const currentImages = (current || []).filter(
            (a: any) => a.type === "IMAGE"
          ) as ExistingAttachment[];
          const currentLinks = (current || []).filter(
            (a: any) => a.type === "FILE"
          ) as ExistingAttachment[];

          // 2) Compute removals (images)
          const removeImageIds = currentImages
            .filter((a) => !keepImageIds.includes(a.id))
            .map((a) => a.id);

          // 3) Guard: final image count must be ≥ 1
          const finalImageCount =
            currentImages.length - removeImageIds.length + newImages.length;
          if (finalImageCount < 1) {
            return { error: { message: "At least one image is required." } };
          }

          // 4) Update base news row
          const { data: updated, error: newsErr } = await supabase
            .from("news")
            .update({ title, content, date })
            .eq("id", id)
            .select()
            .single();
          if (newsErr) throw newsErr;

          // 5) Delete removed IMAGE attachments
          if (removeImageIds.length) {
            const { error: delErr } = await supabase
              .from("news_attachments")
              .delete()
              .in("id", removeImageIds);
            if (delErr) throw delErr;
          }

          // 6) Upload NEW images → insert IMAGE attachments
          if (newImages.length) {
            const toInsert: any[] = [];
            for (const file of newImages) {
              const path = `news/${Date.now()}_${file.name}`;
              const { error: upErr } = await supabase.storage
                .from("images")
                .upload(path, file);
              if (upErr) throw upErr;
              const { publicUrl } = supabase.storage
                .from("images")
                .getPublicUrl(path).data;
              toInsert.push({
                news_id: id,
                type: "IMAGE",
                file_url: publicUrl,
                alias: null,
              });
            }
            const { error: insErr } = await supabase
              .from("news_attachments")
              .insert(toInsert);
            if (insErr) throw insErr;
          }

          // 7) Handle LINK (max 1): create/update/remove
          const existingLink = currentLinks[0]; // you treat it as single
          if (linkAttachment === null && existingLink) {
            await supabase
              .from("news_attachments")
              .delete()
              .eq("id", existingLink.id);
          } else if (linkAttachment && existingLink) {
            const { error: linkUpdErr } = await supabase
              .from("news_attachments")
              .update({
                alias: linkAttachment.alias,
                file_url: linkAttachment.url,
              })
              .eq("id", existingLink.id);
            if (linkUpdErr) throw linkUpdErr;
          } else if (linkAttachment && !existingLink) {
            const { error: linkInsErr } = await supabase
              .from("news_attachments")
              .insert({
                news_id: id,
                type: "FILE",
                file_url: linkAttachment.url,
                alias: linkAttachment.alias,
              });
            if (linkInsErr) throw linkInsErr;
          }

          return { data: updated };
        } catch (error: any) {
          return { error: { message: error.message || "Update failed" } };
        }
      },
      invalidatesTags: ["updates"],
    }),
    changeNewsStatus: build.mutation<any, { id: string; is_active: boolean }>({
      queryFn: async ({ id, is_active }) => {
        try {
          const { data, error } = await supabase
            .from("news")
            .update({ is_active })
            .eq("id", id)
            .select()
            .single();
          if (error) throw error;
          return { data };
        } catch (e: any) {
          return { error: { message: e.message } };
        }
      },
      invalidatesTags: ["updates"],
    }),
    addEvent: build.mutation<
      any,
      {
        title: string;
        content: string;
        date: Date;
        images: File[];
        linkAlias: string;
        linkUrl: string;
      }
    >({
      queryFn: async ({ title, content, date, images, linkAlias, linkUrl }) => {
        try {
          // 1. Insert the event entry
          const { data: eventData, error: eventError } = await supabase
            .from("events")
            .insert([{ title, description: content, datetime: date }])
            .select()
            .single();

          if (eventError) throw eventError;

          const eventId = eventData.id;

          // 2. Upload images to Supabase storage
          const imageAttachmentPromises = images.map(async (image) => {
            const filePath = `events/${Date.now()}_${image.name}`;

            const { error: storageError } = await supabase.storage
              .from("images")
              .upload(filePath, image);

            if (storageError) throw storageError;

            const { publicUrl } = supabase.storage
              .from("images")
              .getPublicUrl(filePath).data;

            return {
              events_id: eventId,
              file_url: publicUrl,
              type: "IMAGE",
              alias: null,
            };
          });

          const imageAttachments = await Promise.all(imageAttachmentPromises);

          // 3. Prepare the link attachment
          const linkAttachment = {
            events_id: eventId,
            file_url: linkUrl,
            type: "FILE",
            alias: linkAlias,
          };

          // 4. Insert all attachments (images + link)
          const allAttachments = [...imageAttachments, linkAttachment];

          const { error: attachmentError } = await supabase
            .from("events_attachments")
            .insert(allAttachments);

          if (attachmentError) throw attachmentError;

          // ✅ Return the event data
          return { data: eventData };
        } catch (error: any) {
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: ["updates"],
    }),
    getEventById: build.query<any, string>({
      queryFn: async (id: string) => {
        const { data, error } = await supabase
          .from("events")
          .select(`*, attachments:events_attachments (*)`)
          .eq("id", id)
          .single();
        if (error) return { error: { message: error.message } };
        return { data };
      },
      providesTags: ["updates"],
    }),

    updateEvent: build.mutation<
      any,
      {
        id: string;
        title: string;
        content: string; // maps to events.description
        date: Date; // maps to events.datetime
        keepImageIds: string[];
        newImages: File[];
        linkAttachment?: { id?: string; alias: string; url: string } | null;
      }
    >({
      queryFn: async ({
        id,
        title,
        content,
        date,
        keepImageIds,
        newImages,
        linkAttachment,
      }) => {
        try {
          // 1) Read current attachments
          const { data: current } = await supabase
            .from("events_attachments")
            .select("*")
            .eq("events_id", id);

          const currentImages = (current || []).filter(
            (a: any) => a.type === "IMAGE"
          ) as ExistingAttachment[];
          const currentLinks = (current || []).filter(
            (a: any) => a.type === "FILE"
          ) as ExistingAttachment[];

          // 2) Compute removals
          const removeImageIds = currentImages
            .filter((a) => !keepImageIds.includes(a.id))
            .map((a) => a.id);

          // 3) Guard ≥1 image
          const finalImageCount =
            currentImages.length - removeImageIds.length + newImages.length;
          if (finalImageCount < 1) {
            return { error: { message: "At least one image is required." } };
          }

          // 4) Update base event
          const { data: updated, error: evErr } = await supabase
            .from("events")
            .update({ title, description: content, datetime: date })
            .eq("id", id)
            .select()
            .single();
          if (evErr) throw evErr;

          // 5) Delete removed images
          if (removeImageIds.length) {
            const { error: delErr } = await supabase
              .from("events_attachments")
              .delete()
              .in("id", removeImageIds);
            if (delErr) throw delErr;
          }

          // 6) Upload new images → insert IMAGE attachments
          if (newImages.length) {
            const toInsert: any[] = [];
            for (const file of newImages) {
              const path = `events/${Date.now()}_${file.name}`;
              const { error: upErr } = await supabase.storage
                .from("images")
                .upload(path, file);
              if (upErr) throw upErr;
              const { publicUrl } = supabase.storage
                .from("images")
                .getPublicUrl(path).data;
              toInsert.push({
                events_id: id,
                type: "IMAGE",
                file_url: publicUrl,
                alias: null,
              });
            }
            const { error: insErr } = await supabase
              .from("events_attachments")
              .insert(toInsert);
            if (insErr) throw insErr;
          }

          // 7) Link (single)
          const existingLink = currentLinks[0];
          if (linkAttachment === null && existingLink) {
            const { error: rmErr } = await supabase
              .from("events_attachments")
              .delete()
              .eq("id", existingLink.id);
            if (rmErr) throw rmErr;
          } else if (linkAttachment && existingLink) {
            const { error: updErr } = await supabase
              .from("events_attachments")
              .update({
                alias: linkAttachment.alias,
                file_url: linkAttachment.url,
              })
              .eq("id", existingLink.id);
            if (updErr) throw updErr;
          } else if (linkAttachment && !existingLink) {
            const { error: insLinkErr } = await supabase
              .from("events_attachments")
              .insert({
                events_id: id,
                type: "FILE",
                file_url: linkAttachment.url,
                alias: linkAttachment.alias,
              });
            if (insLinkErr) throw insLinkErr;
          }

          return { data: updated };
        } catch (error: any) {
          return { error: { message: error.message || "Update failed" } };
        }
      },
      invalidatesTags: ["updates"],
    }),

    changeEventStatus: build.mutation<any, { id: string; is_active: boolean }>({
      queryFn: async ({ id, is_active }) => {
        try {
          const { data, error } = await supabase
            .from("events")
            .update({ is_active })
            .eq("id", id)
            .select()
            .single();
          if (error) throw error;
          return { data };
        } catch (e: any) {
          return { error: { message: e.message } };
        }
      },
      invalidatesTags: ["updates"],
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetNewsQuery,
  useGetEventsQuery,
  useUpdateNewsMutation,
  useChangeNewsStatusMutation,
  useAddNewsMutation,
  useAddEventMutation,
  useGetEventByIdQuery,
  useUpdateEventMutation,
  useChangeEventStatusMutation,
} = updatesAPI;
